import { useEffect, useRef, useState } from 'react';

function storageKey(roundId) {
  return `mini_tasks_countdown:${roundId}`;
}

export function clearMiniTaskCountdown(roundId) {
  try {
    window.localStorage.removeItem(storageKey(roundId));
  } catch (_) {
    // The game also works when storage is unavailable.
  }
}

function readOrStartDeadline(roundId, boardSessionId, durationSeconds) {
  const key = storageKey(roundId);
  try {
    const saved = JSON.parse(window.localStorage.getItem(key) || 'null');
    if (saved?.board_session_id === boardSessionId && Number.isFinite(saved?.deadline_at)) {
      return saved.deadline_at;
    }
  } catch (_) {
    // Start a fresh local deadline.
  }
  const deadline = Date.now() + durationSeconds * 1000;
  try {
    window.localStorage.setItem(key, JSON.stringify({ board_session_id: boardSessionId, deadline_at: deadline }));
  } catch (_) {
    // The current page still keeps its timer.
  }
  return deadline;
}

export default function useMiniTaskCountdown({ roundId, boardSessionId, durationSeconds = 120, active, onExpire, persist = true }) {
  const [remainingSeconds, setRemainingSeconds] = useState(Math.max(0, Number(durationSeconds) || 120));
  const callbackRef = useRef(onExpire);
  const localDeadlineRef = useRef({ roundId: null, boardSessionId: null, deadline: null });
  callbackRef.current = onExpire;

  useEffect(() => {
    if (!active || !roundId || !boardSessionId) return undefined;
    const duration = Math.max(1, Number(durationSeconds) || 120);
    if (localDeadlineRef.current.roundId !== roundId
      || localDeadlineRef.current.boardSessionId !== boardSessionId) {
      localDeadlineRef.current = { roundId, boardSessionId, deadline: null };
    }
    if (!persist && localDeadlineRef.current.deadline == null) {
      localDeadlineRef.current.deadline = Date.now() + duration * 1000;
    }
    const deadline = persist
      ? readOrStartDeadline(roundId, boardSessionId, duration)
      : localDeadlineRef.current.deadline;
    let expired = false;
    const tick = () => {
      const seconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemainingSeconds(seconds);
      if (seconds === 0 && !expired) {
        expired = true;
        callbackRef.current?.();
      }
    };
    tick();
    const interval = window.setInterval(tick, 150);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [active, boardSessionId, durationSeconds, persist, roundId]);

  return remainingSeconds;
}
